/**
 * 見積依頼書の送信設定（#220、docs/88 §4.2・Figma ②改2）を人ごとに覚える（この端末の localStorage。キーに人の番号を入れる）。
 *   ・送信設定の段の開閉（既定は閉じた形）：gb.rfq.sendOpen.<人>
 *   ・「次回も同じ設定」：オンのあいだ、送信方法・控え Bcc・写真台帳（と枚数）・{takeoff}・回答記入欄・条件欄・送付状・窓付封筒（#223）を gb.rfq.sendPrefs.<人> に覚える。
 *     新しく作った下書き（この端末で作成した直後＝markNewRfq の印がある）を開いたときだけ、覚えた値を当てる。
 *     送信方法と控え Bcc は画面の中だけの値なので、どの下書きを開いても覚えた値で始める。オフにすると覚えた値を消す
 *   ・どれも読み書きに失敗しても（プライベートの窓・保存の無効）画面はふつうに動く＝既定の形
 */
export type RfqSendPrefs = {
  via: "email" | "print";
  bcc: boolean;
  includePhotos: boolean;
  photosPerPage: 3 | 6;
  includeTakeoffs: boolean;
  printAnswerBox: boolean;
  printTerms: boolean;
  /** 送付状（#223、docs/89 §5.2）：送信方法が印刷・FAX なら既定オン、メールなら既定オフ。窓付封筒は送付状がオンのときだけ（★8） */
  cover: boolean;
  coverWindow: boolean;
};

const OPEN = "gb.rfq.sendOpen.";
const PREFS = "gb.rfq.sendPrefs.";
const NEW = "gb.rfq.new.";

const get = (store: () => Storage, key: string): string | null => {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
};
const set = (store: () => Storage, key: string, value: string | null): void => {
  try {
    if (value == null) store().removeItem(key);
    else store().setItem(key, value);
  } catch {
    /* 覚えられなくても画面は動く */
  }
};
const local = () => window.localStorage;
const session = () => window.sessionStorage;

/** 送信設定の段を開いているか（既定は閉じた形） */
export const readSendOpen = (account: string | null): boolean => (account ? get(local, OPEN + account) === "1" : false);
export const writeSendOpen = (account: string | null, open: boolean): void => {
  if (account) set(local, OPEN + account, open ? "1" : null);
};

/** 「次回も同じ設定」で覚えた値（オフ＝null） */
export function readSendPrefs(account: string | null): RfqSendPrefs | null {
  if (!account) return null;
  const raw = get(local, PREFS + account);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<RfqSendPrefs>;
    if (v.via !== "email" && v.via !== "print") return null;
    return {
      via: v.via,
      bcc: v.bcc !== false,
      includePhotos: v.includePhotos !== false,
      photosPerPage: v.photosPerPage === 6 ? 6 : 3,
      includeTakeoffs: v.includeTakeoffs === true,
      printAnswerBox: v.printAnswerBox !== false,
      printTerms: v.printTerms !== false,
      cover: typeof v.cover === "boolean" ? v.cover : v.via === "print",
      coverWindow: v.cover !== false && v.coverWindow === true,
    };
  } catch {
    return null;
  }
}
export const writeSendPrefs = (account: string | null, prefs: RfqSendPrefs | null): void => {
  if (account) set(local, PREFS + account, prefs ? JSON.stringify(prefs) : null);
};

/** 下書きを作った直後の印（作った画面が付け、作成画面が1回だけ読んで消す。このタブの中だけ） */
export const markNewRfq = (id: string): void => set(session, NEW + id, "1");
export function takeNewRfq(id: string): boolean {
  const had = get(session, NEW + id) === "1";
  if (had) set(session, NEW + id, null);
  return had;
}
